// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * The Satellites layer: what the viewer chose (on/off, which objects,
 * colour by orbit, country or purpose, hidden legend entries), the data
 * behind it (the GP mirror, the optional catalogue and events), the clock,
 * and the selected object. One per page; the browser globes, the headset
 * and the phone all draw from it, and a room carries its choices.
 *
 * Plan: docs/AYNI_XR_SATELLITES_PLAN.md.
 */
import { SatelliteClock } from './satelliteClock'
import { SatelliteEngine, type SatellitePropagatorPort } from './satelliteEngine'
import {
  fetchEvents,
  fetchGroupBytes,
  fetchGroupIds,
  fetchPointer,
  fetchSatcat,
  fetchSources,
  isStale,
  POLL_MS,
} from './satelliteFeed'
import {
  COUNTRY_COLOURS,
  COUNTRY_GROUPS,
  countryGroup,
  DEBRIS_COLOUR,
  hexToRgb01,
  ORBIT_CLASSES,
  ORBIT_COLOURS,
  PURPOSE_COLOURS,
  PURPOSES,
  type SatellitePurpose,
} from './satelliteOrbit'
import { buildPastView, type PastView } from './pastOrbit'
import { FOOTPRINT_FLOATS, swathOf, writeGroundCircle, writeHorizonFootprint } from './footprint'
import { GLOBE_RADIUS_KM } from './satelliteOrbit'
import type { GpMirrorPointer, SatcatRecord, SatelliteEvent, SatelliteInfo, SatelliteSourcesFile, SourceDatasetEntry, SourcePlatformEntry } from './satelliteTypes'
import { sourcesAt, type DsaPlatform } from './sourcePlatforms'

export const DEBRIS_GROUPS = ['fengyun-1c-debris', 'cosmos-2251-debris', 'iridium-33-debris'] as const
export type DebrisGroup = (typeof DEBRIS_GROUPS)[number]

/** What to show. "highlights" = stations, weather, Earth observation, navigation and the GEO belt. */
/** `with-datasets` (owner, 2026-10-24): every tracked satellite that feeds a dataset of the catalog (`sources.json`). */
export type ShowSet = 'highlights' | 'all' | 'starlink' | 'with-datasets' | DebrisGroup
export const SHOW_SETS: readonly ShowSet[] = ['highlights', 'all', 'starlink', 'with-datasets', ...DEBRIS_GROUPS]
/** The members key the catalog's satellites are kept under (NORAD ids). */
export const CATALOG_MEMBERS = 'with-datasets'

export type ColourBy = 'orbit' | 'country' | 'purpose'
export const COLOUR_BYS: readonly ColourBy[] = ['orbit', 'country', 'purpose']

/** Height of the orbit shell in the headset: true scale, or squeezed so GEO stays within reach. */
export type AltitudeScale = 'true' | 'compact'

/**
 * Height on the browser globe (owner, 2026-10-22): on the ground (the point
 * under each satellite), or floating at its height, true or compact.
 */
export type MapHeight = 'ground' | AltitudeScale
export const MAP_HEIGHTS: readonly MapHeight[] = ['ground', 'compact', 'true']

export interface SatelliteSettings {
  on: boolean
  show: ShowSet[]
  colourBy: ColourBy
  /** Legend entries switched off (category keys of the current colouring). */
  hidden: string[]
  altitude: AltitudeScale
  /** The browser globe's height (the flat map is always on the ground). */
  mapHeight: MapHeight
  trails: boolean
  /** The card's settings folded away under its title (owner, 2026-10-22). */
  collapsed: boolean
  /** What the data's satellites and the selected one see: the horizon circle on the ground (owner, 2026-10-24). */
  fov: boolean
}

export const DEFAULT_SETTINGS: SatelliteSettings = {
  on: false,
  show: ['highlights'],
  colourBy: 'orbit',
  hidden: [],
  altitude: 'true',
  mapHeight: 'ground',
  trails: true,
  collapsed: false,
  fov: true,
}

/** The small groups whose members make "highlights" and give a purpose without the catalogue. */
const MEMBERSHIP_GROUPS: Record<string, SatellitePurpose> = {
  stations: 'human-spaceflight',
  weather: 'weather',
  resource: 'earth-observation',
  gnss: 'navigation',
}

const STORAGE_KEY = 'ayni.satellites'

export type SatelliteStatus = 'idle' | 'loading' | 'ready' | 'error'

export interface LegendEntry {
  key: string
  colour: string
  /** Objects in this category among those the show sets select. */
  count: number
  hidden: boolean
}

export interface SelectedOrbit {
  /** Unique per set of paths: what the renderers key their uploads on. */
  id: number
  /** The object's index; -1 for the last orbit of an object no longer in the data (a space event). */
  index: number
  /** One period, Earth-fixed km, as an ellipse through the object (headset). */
  ellipse: Float32Array
  /** One period of ground track centred on now, Earth-fixed km (globe/map). */
  track: Float32Array
  /** Sim time the paths were computed for. */
  at: number
  /** Pairs of points drawn with the paths: the ring and pin where an object came down. */
  marks?: Float32Array
}

/**
 * A catalog satellite's next pass over the viewer (AR, Sky above me; step 3
 * of the catalog plan): worked out by the sky, listed with Load by the
 * cards. `overhead`: above 10° right now.
 */
export interface SkyPass {
  norad: number
  name: string
  riseMs: number
  maxElDeg: number
  overhead: boolean
  /** The datasets it feeds, by the app's ids (`appDatasetId`), first the one named on the line. */
  datasets: string[]
  /** The line the cards show. */
  text: string
}

/** The space event whose object is no longer in the data, and what is known of it. */
export interface EventFocus {
  event: SatelliteEvent
  /** Null while the orbit is being worked out, or when the event carries no elements. */
  view: PastView | null
  loading: boolean
}

let orbitIds = 0

/**
 * AYNI (owner, 2026-10-23): what the app tells the layer about the
 * datasets on the globes: their platforms (DSA 1.10, `sourcePlatforms.ts`)
 * and the frame's instant, so the satellites that imaged the picture
 * can be marked.
 */
export interface SourceInput {
  platforms: readonly DsaPlatform[]
  /** The primary frame's instant, epoch ms; null = no date (every window holds). */
  atMs: number | null
}

/** A satellite the current data came from, as the layer draws and lists it. */
export interface SourceSatellite {
  readonly platform: DsaPlatform
  readonly norad: number
  /** Its index in the engine, or -1 when the GP mirror does not carry it (listed by name, not drawn). */
  readonly index: number
  /** A `fallback` stand-in held back while its primary is active (VPS, 2026-10-24): listed dimmed, not drawn. */
  readonly standby: boolean
  /**
   * The frame is days away from the orbits' epoch (VPS, 2026-10-24: never
   * draw a satellite at today's position for an old frame; GOES-15 is
   * tracked as EWS-G2, far from its old slot): named on the card, not pulsed.
   */
  readonly dated: boolean
}

/** How far from the orbits' epoch a frame may be and still be drawn at today's position. */
export const SOURCE_FRESH_MS = 5 * 86_400_000
/** Geostationary height, km, for a dated source drawn at its nominal longitude. */
const GEO_ALT_KM = 35_786
/** The ring marking that slot on the ground, km. */
const NOMINAL_MARK_KM = 350

/** How often the layer asks the app for its sources (the frame's date moves). */
const SOURCE_REFRESH_MS = 500

export function loadSettings(storage: Storage | null = safeStorage()): SatelliteSettings {
  try {
    const raw = storage?.getItem(STORAGE_KEY)
    if (!raw) return { ...DEFAULT_SETTINGS }
    return sanitiseSettings(JSON.parse(raw))
  } catch {
    return { ...DEFAULT_SETTINGS }
  }
}

export function sanitiseSettings(v: unknown): SatelliteSettings {
  const o = (v && typeof v === 'object' ? v : {}) as Partial<SatelliteSettings>
  const show = Array.isArray(o.show) ? o.show.filter((s): s is ShowSet => SHOW_SETS.includes(s as ShowSet)) : null
  return {
    on: typeof o.on === 'boolean' ? o.on : DEFAULT_SETTINGS.on,
    show: show && show.length ? [...new Set(show)] : [...DEFAULT_SETTINGS.show],
    colourBy: COLOUR_BYS.includes(o.colourBy as ColourBy) ? (o.colourBy as ColourBy) : DEFAULT_SETTINGS.colourBy,
    hidden: Array.isArray(o.hidden) ? o.hidden.filter((h): h is string => typeof h === 'string').slice(0, 32) : [],
    altitude: o.altitude === 'compact' ? 'compact' : 'true',
    mapHeight: MAP_HEIGHTS.includes(o.mapHeight as MapHeight) ? (o.mapHeight as MapHeight) : 'ground',
    trails: typeof o.trails === 'boolean' ? o.trails : true,
    collapsed: o.collapsed === true,
    fov: typeof o.fov === 'boolean' ? o.fov : DEFAULT_SETTINGS.fov,
  }
}

function safeStorage(): Storage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage
  } catch {
    return null
  }
}

/** The legend keys of a colouring, in order. */
export function categoryKeys(by: ColourBy): string[] {
  if (by === 'orbit') return [...ORBIT_CLASSES, 'debris']
  if (by === 'country') return [...COUNTRY_GROUPS, 'debris']
  return [...PURPOSES]
}

export function categoryColour(by: ColourBy, key: string): string {
  if (key === 'debris') return by === 'purpose' ? PURPOSE_COLOURS.debris : DEBRIS_COLOUR
  if (by === 'orbit') return ORBIT_COLOURS[key as keyof typeof ORBIT_COLOURS] ?? '#d8d8d8'
  if (by === 'country') return COUNTRY_COLOURS[key as keyof typeof COUNTRY_COLOURS] ?? '#d8d8d8'
  return PURPOSE_COLOURS[key as SatellitePurpose] ?? '#d8d8d8'
}

export function isDebris(info: SatelliteInfo): boolean {
  return info.type === 'DEB' || (DEBRIS_GROUPS as readonly string[]).includes(info.source)
}

export function categoryOf(info: SatelliteInfo, by: ColourBy): string {
  if (isDebris(info)) return 'debris'
  if (by === 'orbit') return info.orbit
  if (by === 'country') return info.country
  return info.purpose ?? 'other'
}

/** Does any chosen show set select this object? */
export function inShowSets(info: SatelliteInfo, show: readonly ShowSet[], members: ReadonlyMap<string, Set<number>>): boolean {
  for (const s of show) {
    // The catalog's satellites (owner, 2026-10-24): their own set, and part of Highlights (decision C6).
    if ((s === CATALOG_MEMBERS || s === 'highlights') && members.get(CATALOG_MEMBERS)?.has(info.norad)) return true
    if (s === 'all' && info.source === 'active') return true
    if (s === 'starlink' && info.starlink) return true
    if (s === info.source) return true
    if (s === 'highlights' && !info.starlink && info.source === 'active') {
      if (info.orbit === 'geo') return true
      for (const g of Object.keys(MEMBERSHIP_GROUPS)) if (members.get(g)?.has(info.norad)) return true
    }
  }
  return false
}

export class SatelliteLayer {
  readonly clock = new SatelliteClock()
  readonly engine: SatelliteEngine
  settings: SatelliteSettings
  status: SatelliteStatus = 'idle'
  error: string | null = null
  pointer: GpMirrorPointer | null = null
  events: SatelliteEvent[] = []
  /** Set when satcat.json was read. */
  hasCatalogue = false
  selected: number | null = null
  /**
   * "Sky above me" (AR only): asked for here by any control, run by the
   * session. Never saved: it needs the viewer's place, which stays in the
   * session.
   */
  skyOn = false
  /**
   * "Fully VR" (owner, 2026-10-22): hide the room and float in space — the
   * Milky Way, the bright stars and the Sun all the way round the globe,
   * turning with the clock and with the globe, and the satellites on their
   * orbits around it. AYNI (owner, 2026-11-03): no longer part of Sky above
   * me (which stands at the viewer's place on the real sky); the two are
   * different views, so turning one on turns the other off. Needs no place
   * or north. Never saved.
   */
  fullVrOn = false
  /** Set by an AR session that can show the sky (passthrough). */
  skyAvailable = false
  /** The sky's own line ("Face north and press…", "12 above you…"), set by the session. */
  skyStatus: string | null = null
  /** Bumped by "Set north": the session asks for north again. */
  northRequest = 0
  /** The catalog's satellites passing over the viewer (Sky above me), soonest first; empty off the sky. */
  skyPasses: readonly SkyPass[] = []
  selectedOrbit: SelectedOrbit | null = null
  /**
   * A space event whose object has left the data (owner, 2026-10-24: "the
   * users will click on it and be disappointed if nothing happens"): its card
   * and, when the event says them, its last orbit and where it came down.
   * Never together with a selected object.
   */
  eventFocus: EventFocus | null = null
  /**
   * AYNI (owner, 2026-10-23): the satellites the current data came from
   * (DSA 1.10 platforms), in the order the descriptor writes them. Drawn
   * with a pulse whatever the show sets, listed on the card.
   */
  sources: readonly SourceSatellite[] = []
  private sourceIndexes = new Set<number>()
  /**
   * The satellites <-> datasets index (`sources.json`, owner 2026-10-24): which
   * datasets each satellite feeds. Read with the catalogue files, and on
   * request while the layer is off (Browse's "Data from" line).
   */
  sourcesIndex: SatelliteSourcesFile | null = null
  private sourcesIndexAsked: Promise<SatelliteSourcesFile | null> | null = null
  /** Engine indexes of the catalog's satellites (the steady ring). */
  private catalogIndexes = new Set<number>()
  /**
   * What the data's satellites and the selected object see (step 2 of the
   * catalog plan): their horizon circles on the ground, as pairs of
   * Earth-fixed points, rewritten each tick (`footprint.ts`). Empty with
   * the setting off. Renderers key their upload on `footprintsVersion`.
   */
  footprints = new Float32Array(0)
  footprintsVersion = 0
  private sourceProvider: (() => SourceInput) | null = null
  private sourceKey = ''
  private sourceCheckedAt = 0
  private sourceTimer: ReturnType<typeof setInterval> | null = null
  /**
   * The body the globe shows (offEarthBody.ts), lower case, or null for the
   * Earth; told by the app as the main globe's dataset changes (owner,
   * 2026-10-23). Satellites orbit the Earth, so with Saturn, the Moon, the
   * Sun or any other body on the globe none are drawn, on any surface, and
   * the switch is held. The viewer's choice (`on`) is kept and comes back
   * with the next dataset of the Earth. "Sky above me" is the viewer's own
   * sky and is not held.
   */
  body: string | null = null

  private members = new Map<string, Set<number>>()
  private satcat: Record<string, SatcatRecord> | null = null
  private loadedSources = new Set<string>()
  private loading: Promise<void> | null = null
  private pollTimer: ReturnType<typeof setInterval> | null = null
  private listeners = new Set<() => void>()
  private frameListeners = new Set<() => void>()
  private raf = 0
  private orbitPending = false
  private readonly storage: Storage | null
  private readonly fetcher: typeof fetch | undefined

  constructor(worker: SatellitePropagatorPort, opts: { storage?: Storage | null; fetcher?: typeof fetch } = {}) {
    this.engine = new SatelliteEngine(worker)
    this.storage = opts.storage === undefined ? safeStorage() : opts.storage
    this.fetcher = opts.fetcher
    this.settings = loadSettings(this.storage)
    this.engine.onChange(() => this.emitFrame())
  }

  get on(): boolean {
    return this.settings.on
  }

  /** The globe shows the Earth, the one body these satellites orbit. */
  get onEarth(): boolean {
    return this.body === null
  }

  /** Drawn on the globes, in the headset and on the phone: on, and the Earth shown. */
  get shown(): boolean {
    return this.settings.on && this.body === null
  }

  /**
   * The app says which body the main globe shows (null, '' or 'earth': the
   * Earth). Another body closes the selected object's card.
   */
  setBody(body: string | null | undefined): void {
    const named = body?.trim().toLowerCase() || null
    const next = named === 'earth' ? null : named
    if (next === this.body) return
    this.body = next
    if (next !== null) {
      this.select(null)
      this.closeEvent()
    }
    this.emit()
  }

  get stale(): boolean {
    return !!this.pointer && isStale(this.pointer.updatedAt)
  }

  get attribution(): string | null {
    return this.pointer?.attribution ?? null
  }

  /** Changes settings, saves them, and loads what they need. */
  update(patch: Partial<SatelliteSettings>, opts: { save?: boolean } = {}): void {
    const next = sanitiseSettings({ ...this.settings, ...patch })
    if (patch.colourBy && patch.colourBy !== this.settings.colourBy && !('hidden' in patch)) next.hidden = []
    this.settings = next
    // A room's lead's choices are shown, not kept as this viewer's own.
    if (opts.save !== false) this.save()
    else this.followed = true
    if (next.on) void this.ensureLoaded()
    else this.stop()
    if (!next.on) {
      this.select(null)
      this.closeEvent()
    }
    this.applyStyle()
    this.emit()
  }

  /** The sky says which catalog satellites pass over the viewer next. */
  setSkyPasses(passes: readonly SkyPass[]): void {
    this.skyPasses = passes
    this.emit()
  }

  setSky(on: boolean): void {
    const next = on && this.skyAvailable
    if (next === this.skyOn) return
    this.skyOn = next
    if (!next) this.skyPasses = []
    // AYNI (owner, 2026-11-03): one view at a time; Fully VR is space, not the sky from here.
    if (next) this.fullVrOn = false
    if (next && !this.settings.on) this.update({ on: true })
    this.emit()
  }

  /**
   * Fully VR (floating in space): in a session that can hide the room; it
   * turns Sky above me off, and the satellites on, since their orbits are
   * part of the view.
   */
  setFullVr(on: boolean): void {
    const next = on && this.skyAvailable
    if (next === this.fullVrOn) return
    this.fullVrOn = next
    if (next) {
      this.skyOn = false
      this.skyPasses = []
      if (!this.settings.on) this.update({ on: true })
    }
    this.emit()
  }

  /** The session says whether it can show the sky; leaving one turns the sky off. */
  setSkyAvailable(available: boolean): void {
    this.skyAvailable = available
    if (!available) {
      this.skyOn = false
      this.fullVrOn = false
      this.skyStatus = null
      this.skyPasses = []
    }
    this.emit()
  }

  requestNorth(): void {
    this.northRequest++
    this.emit()
  }

  setSkyStatus(text: string | null): void {
    if (text === this.skyStatus) return
    this.skyStatus = text
    this.emit()
  }

  /** Whether what shows came from a room's lead rather than this viewer. */
  followed = false

  private save(): void {
    this.followed = false
    try {
      this.storage?.setItem(STORAGE_KEY, JSON.stringify(this.settings))
    } catch {
      /* storage full or blocked: the choice still holds for this visit */
    }
  }

  /** Back to this viewer's own saved choices (after following a room's lead). */
  restoreOwn(): void {
    if (!this.followed) return
    this.followed = false
    this.update(loadSettings(this.storage), { save: false })
    this.followed = false
    this.clock.goLive()
  }

  setOn(on: boolean): void {
    this.update({ on })
  }

  toggleHidden(key: string): void {
    const hidden = new Set(this.settings.hidden)
    if (hidden.has(key)) hidden.delete(key)
    else hidden.add(key)
    this.update({ hidden: [...hidden] })
  }

  toggleShow(set: ShowSet): void {
    const show = new Set(this.settings.show)
    if (show.has(set)) show.delete(set)
    else show.add(set)
    // An empty choice would hide everything while still "on": keep highlights.
    if (!show.size) show.add('highlights')
    this.update({ show: [...show] })
  }

  /**
   * The app says where the data on the globes came from (DSA 1.10
   * platforms) and the frame's instant; the layer asks again every
   * half second while it runs, as the frame's date moves.
   */
  setSourceProvider(fn: (() => SourceInput) | null): void {
    this.sourceProvider = fn
    if (this.sourceTimer) clearInterval(this.sourceTimer)
    this.sourceTimer = null
    // Off, nothing ticks: a slow poll keeps the card's "satellites behind
    // this data" line true as datasets come and go. On, the tick does it.
    if (fn && typeof setInterval !== 'undefined') {
      this.sourceTimer = setInterval(() => {
        if (!this.settings.on) this.refreshSources()
      }, SOURCE_REFRESH_MS * 2)
    }
    this.refreshSources(true)
  }

  /** Is the object at this engine index one of the data's sources? */
  isSource(index: number): boolean {
    return this.sourceIndexes.has(index)
  }

  /** Does the object at this engine index feed any dataset of the catalog (the steady ring)? */
  isCatalogSource(index: number): boolean {
    return this.catalogIndexes.has(index)
  }

  /** The datasets a satellite feeds, from `sources.json` (empty without it). */
  datasetsOf(norad: number): readonly SourceDatasetEntry[] {
    return this.sourcesIndex?.platforms[`norad:${norad}`]?.datasets ?? []
  }

  /** What `sources.json` knows of a satellite: tracked, launch, decay (null without it). */
  platformOf(norad: number): SourcePlatformEntry | null {
    return this.sourcesIndex?.platforms[`norad:${norad}`] ?? null
  }

  /** The satellites a dataset is built from, by catalog id (empty without the index). */
  satellitesOfDataset(datasetId: string): readonly SourcePlatformEntry[] {
    const index = this.sourcesIndex
    if (!index) return []
    const keys = index.datasets[datasetId]?.platforms ?? []
    return keys.map((k) => index.platforms[k]).filter((p): p is SourcePlatformEntry => !!p)
  }

  /**
   * Reads the index on request, with the layer off (Browse). Once; the
   * layer's own load replaces it when it runs.
   */
  loadSourcesIndex(): Promise<SatelliteSourcesFile | null> {
    if (this.sourcesIndex) return Promise.resolve(this.sourcesIndex)
    if (!this.sourcesIndexAsked) {
      this.sourcesIndexAsked = fetchSources(this.pointer, this.fetcher)
        .then((f) => {
          if (f && !this.sourcesIndex) this.setSourcesIndex(f)
          return this.sourcesIndex
        })
        .catch(() => null)
    }
    return this.sourcesIndexAsked
  }

  private setSourcesIndex(file: SatelliteSourcesFile): void {
    this.sourcesIndex = file
    const norads = new Set<number>()
    for (const p of Object.values(file.platforms)) if (p && p.tracked && typeof p.norad === 'number') norads.add(p.norad)
    this.members.set(CATALOG_MEMBERS, norads)
    this.rebuildCatalogIndexes()
    this.applyStyle()
    this.emit()
  }

  private rebuildCatalogIndexes(): void {
    const norads = this.members.get(CATALOG_MEMBERS)
    const next = new Set<number>()
    if (norads?.size) {
      this.engine.infos.forEach((info, i) => {
        if (norads.has(info.norad)) next.add(i)
      })
    }
    this.catalogIndexes = next
  }

  /**
   * Re-reads the sources: the satellites whose window holds the frame's
   * instant, each matched to the engine by NORAD id. Redraws only when
   * the set changed. Throttled unless forced.
   */
  refreshSources(force = false): void {
    const now = typeof performance !== 'undefined' ? performance.now() : Date.now()
    if (!force && now - this.sourceCheckedAt < SOURCE_REFRESH_MS) return
    this.sourceCheckedAt = now
    const input = this.sourceProvider?.()
    const at = input ? sourcesAt(input.platforms, input.atMs) : []
    const infos = this.engine.infos
    // The orbits are as of the mirror's run (within hours of now): a frame days away is not drawn with them.
    const epoch = this.pointer ? Date.parse(this.pointer.updatedAt) : Date.now()
    const dated = input?.atMs != null && Number.isFinite(epoch) && Math.abs(input.atMs - epoch) > SOURCE_FRESH_MS
    const next: SourceSatellite[] = at.map(({ platform, standby }) => ({
      platform,
      norad: platform.norad!,
      index: infos.findIndex((i) => i.norad === platform.norad),
      standby,
      dated,
    }))
    const key = next.map((s) => `${s.norad}:${s.index}:${s.standby ? 's' : ''}${s.dated ? 'd' : ''}`).join(',')
    if (key === this.sourceKey) return
    this.sourceKey = key
    this.sources = next
    // A stand-in held back, or a satellite on an old frame, is listed, not drawn.
    this.sourceIndexes = new Set(next.filter((s) => s.index >= 0 && !s.standby && !s.dated).map((s) => s.index))
    this.applyStyle()
    this.emit()
  }

  /** Starts loading if the layer is on (call once at start-up). */
  start(): void {
    if (this.settings.on) void this.ensureLoaded()
  }

  private ensureLoaded(): Promise<void> {
    if (!this.loading) {
      this.loading = this.loadAll().finally(() => {
        this.loading = null
      })
    }
    this.startLoop()
    if (!this.pollTimer && typeof setInterval !== 'undefined') {
      this.pollTimer = setInterval(() => void this.poll(), POLL_MS)
    }
    return this.loading
  }

  private async loadAll(): Promise<void> {
    this.status = this.status === 'ready' ? 'ready' : 'loading'
    this.error = null
    this.emit()
    try {
      if (!this.pointer) this.pointer = await fetchPointer(this.fetcher)
      const pointer = this.pointer
      const wanted = ['active', ...this.settings.show.filter((s) => (DEBRIS_GROUPS as readonly string[]).includes(s))]
      for (const source of wanted) {
        if (this.loadedSources.has(source)) continue
        const entry = pointer.groups[source]
        if (!entry) continue
        const bytes = await fetchGroupBytes(entry, this.fetcher)
        await this.engine.loadGroup(source, bytes, entry.sha256)
        this.loadedSources.add(source)
      }
      if (!this.members.size) await this.loadMembers(pointer)
      if (!this.satcat) {
        const [satcat, events, sources] = await Promise.all([
          fetchSatcat(pointer, this.fetcher),
          fetchEvents(pointer, this.fetcher),
          fetchSources(pointer, this.fetcher),
        ])
        this.satcat = satcat?.objects ?? null
        this.hasCatalogue = !!satcat
        this.events = sortEvents(events?.events ?? [])
        if (sources) this.setSourcesIndex(sources)
      }
      this.enrich()
      this.rebuildCatalogIndexes()
      this.status = 'ready'
      // A debris group switched on while this load ran.
      const missing = this.settings.show.some(
        (g) => (DEBRIS_GROUPS as readonly string[]).includes(g) && !this.loadedSources.has(g) && pointer.groups[g],
      )
      if (missing && this.settings.on) {
        await this.loadAll()
        return
      }
    } catch (err) {
      this.status = 'error'
      this.error = err instanceof Error ? err.message : String(err)
    }
    // The objects just loaded: match the data's sources to them again.
    this.sourceKey = ''
    this.refreshSources(true)
    this.applyStyle()
    this.emit()
  }

  private async loadMembers(pointer: GpMirrorPointer): Promise<void> {
    await Promise.all(
      Object.keys(MEMBERSHIP_GROUPS).map(async (g) => {
        const entry = pointer.groups[g]
        if (!entry) return
        try {
          this.members.set(g, await fetchGroupIds(entry, this.fetcher))
        } catch {
          /* highlights fall back to the GEO belt alone */
        }
      }),
    )
  }

  /** Fills owner, country and purpose from the catalogue and the group lists. */
  private enrich(): void {
    for (const info of this.engine.infos) {
      const rec = this.satcat?.[String(info.norad)]
      info.owner = rec?.owner
      info.country = countryGroup(rec?.owner)
      info.type = rec?.type ?? (isDebris(info) ? 'DEB' : undefined)
      info.launch = rec?.launch ?? null
      let purpose: SatellitePurpose | undefined = rec?.purpose
      if (!purpose) {
        for (const [g, p] of Object.entries(MEMBERSHIP_GROUPS)) {
          if (this.members.get(g)?.has(info.norad)) {
            purpose = p
            break
          }
        }
      }
      if (!purpose && info.starlink) purpose = 'communications'
      if (!purpose && isDebris(info)) purpose = 'debris'
      info.purpose = purpose ?? 'other'
    }
  }

  /** Re-reads the pointer; reloads everything when the data moved on. */
  private async poll(): Promise<void> {
    if (!this.settings.on) return
    try {
      const next = await fetchPointer(this.fetcher)
      const prev = this.pointer
      const changed =
        !prev ||
        next.updatedAt !== prev.updatedAt ||
        [...this.loadedSources].some((s) => next.groups[s]?.sha256 !== prev.groups[s]?.sha256)
      if (!changed) return
      this.pointer = next
      this.loadedSources.clear()
      const catalog = this.members.get(CATALOG_MEMBERS)
      this.members.clear()
      if (catalog) this.members.set(CATALOG_MEMBERS, catalog)
      this.satcat = null
      const keep = this.selected !== null ? this.engine.infos[this.selected]?.norad : null
      this.select(null)
      await this.engine.clear()
      await this.ensureLoaded()
      if (keep != null) this.selectNorad(keep)
    } catch {
      /* keep what we have; the next poll tries again */
    }
  }

  /** Recomputes which objects show and their colours. */
  applyStyle(): void {
    const infos = this.engine.infos
    const n = infos.length
    const mask = new Uint8Array(n)
    const colours = new Float32Array(n * 3)
    const { on, show, colourBy, hidden } = this.settings
    const hiddenSet = new Set(hidden)
    const rgb = new Map<string, [number, number, number]>()
    for (let i = 0; i < n; i++) {
      const info = infos[i]
      const key = categoryOf(info, colourBy)
      let c = rgb.get(key)
      if (!c) {
        c = hexToRgb01(categoryColour(colourBy, key))
        rgb.set(key, c)
      }
      colours.set(c, i * 3)
      if (on && !hiddenSet.has(key) && inShowSets(info, show, this.members)) mask[i] = 1
    }
    // The selected object always shows.
    if (this.selected !== null && this.selected < n) mask[this.selected] = 1
    // So do the data's sources (owner, 2026-10-23), whatever the show sets.
    if (on) for (const i of this.sourceIndexes) if (i < n) mask[i] = 1
    this.engine.setStyle(mask, colours)
  }

  legend(): LegendEntry[] {
    const by = this.settings.colourBy
    const counts = new Map<string, number>()
    for (const info of this.engine.infos) {
      if (!inShowSets(info, this.settings.show, this.members)) continue
      const key = categoryOf(info, by)
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    const hidden = new Set(this.settings.hidden)
    return categoryKeys(by)
      .filter((key) => (counts.get(key) ?? 0) > 0)
      .map((key) => ({ key, colour: categoryColour(by, key), count: counts.get(key) ?? 0, hidden: hidden.has(key) }))
  }

  /** How many objects are drawn now. */
  visibleCount(): number {
    let c = 0
    for (const m of this.engine.mask) c += m
    return c
  }

  indexOfNorad(norad: number): number {
    return this.engine.infos.findIndex((i) => i.norad === norad)
  }

  selectNorad(norad: number | null): boolean {
    if (norad === null) {
      this.select(null)
      return true
    }
    const i = this.indexOfNorad(norad)
    if (i < 0) return false
    this.select(i)
    return true
  }

  select(index: number | null): void {
    if (index === this.selected) return
    this.selected = index
    this.selectedOrbit = null
    if (index !== null) this.eventFocus = null
    this.applyStyle()
    this.emit()
    if (index !== null) void this.refreshOrbit()
  }

  /**
   * Opens a space event (owner, 2026-10-24). Turns on what it is about; an
   * object still in the data is selected, with its new orbit. Otherwise the
   * event itself is the focus: its card, and its last orbit with the place it
   * came down when the event carries them.
   */
  focusEvent(e: SatelliteEvent): void {
    const group = e.group as DebrisGroup | undefined
    if (group && (DEBRIS_GROUPS as readonly string[]).includes(group) && !this.settings.show.includes(group)) {
      this.update({ show: [...this.settings.show, group] })
    }
    if (!this.on) this.setOn(true)
    this.eventFocus = null
    for (const n of e.norad ?? []) if (this.selectNorad(n)) return
    this.select(null)
    const elements = e.past?.elements
    const focus: EventFocus = { event: e, view: null, loading: !!elements }
    this.eventFocus = focus
    this.selectedOrbit = null
    this.emit()
    // A debris group still loading: its objects are in the data in a moment.
    if (group && !this.loadedSources.has(group)) {
      void this.ensureLoaded().then(() => {
        if (this.eventFocus !== focus) return
        for (const n of e.norad ?? []) if (this.selectNorad(n)) return
      })
    }
    if (!elements || !e.past) return
    void buildPastView(e.past)
      .catch(() => null)
      .then((view) => {
        if (this.eventFocus !== focus) return
        focus.loading = false
        focus.view = view
        if (view) {
          this.selectedOrbit = { id: ++orbitIds, index: -1, ellipse: view.ellipse, track: view.track, at: view.at, marks: view.marks }
        }
        this.emit()
        this.emitFrame()
      })
  }

  /** Closes an event's card and the paths it drew. */
  closeEvent(): void {
    if (!this.eventFocus) return
    this.eventFocus = null
    if (this.selected === null) this.selectedOrbit = null
    this.emit()
    this.emitFrame()
  }

  get selectedInfo(): SatelliteInfo | null {
    return this.selected === null ? null : (this.engine.infos[this.selected] ?? null)
  }

  /** Keeps the selected object's paths current (called each tick). */
  private async refreshOrbit(): Promise<void> {
    const index = this.selected
    if (index === null || this.orbitPending) return
    const info = this.engine.infos[index]
    if (!info) return
    const t = this.clock.time()
    const periodMs = info.periodMin * 60_000
    const prev = this.selectedOrbit
    if (prev && prev.index === index && Math.abs(t - prev.at) < Math.min(periodMs / 24, 600_000)) return
    this.orbitPending = true
    try {
      const samples = 241
      const [ellipse, track] = await Promise.all([
        this.engine.orbitPath(index, t, t + periodMs, samples, t),
        this.engine.orbitPath(index, t - periodMs / 2, t + periodMs / 2, samples),
      ])
      if (this.selected === index) {
        this.selectedOrbit = { id: ++orbitIds, index, ellipse, track, at: t }
        this.emitFrame()
      }
    } catch {
      /* the next tick tries again */
    } finally {
      this.orbitPending = false
    }
  }

  /** The horizon circles of the data's sources and the selected object, from where they are now. */
  private refreshFootprints(): void {
    /** Live: an engine index and its instrument's swath. Nominal: a dated geostationary source at its slot. */
    const live: Array<{ index: number; swath: number | null }> = []
    const nominal: number[] = []
    if (this.settings.fov && this.shown) {
      for (const s of this.sources) {
        if (s.standby) continue
        if (s.index >= 0 && !s.dated) live.push({ index: s.index, swath: swathOf(s.platform.instrument) })
        // An old frame (VPS, 2026-10-24): a geostationary source is drawn at its nominal longitude, not where it is today.
        else if (s.dated && s.platform.nominalLongitude !== null) nominal.push(s.platform.nominalLongitude)
      }
      const sel = this.selected
      if (sel !== null && !live.some((l) => l.index === sel)) {
        const info = this.engine.infos[sel]
        live.push({ index: sel, swath: swathOf(info ? this.datasetsOf(info.norad)[0]?.instrument : null) })
      }
    }
    // A nominal slot gets its disk and a small ring at the sub-satellite point.
    const floats = live.length * FOOTPRINT_FLOATS + nominal.length * 2 * FOOTPRINT_FLOATS
    if (!floats) {
      if (this.footprints.length) {
        this.footprints = new Float32Array(0)
        this.footprintsVersion++
      }
      return
    }
    const cur = this.engine.current
    if (this.footprints.length !== floats) this.footprints = new Float32Array(floats)
    let o = 0
    for (const l of live) {
      writeHorizonFootprint(cur[l.index * 3], cur[l.index * 3 + 1], cur[l.index * 3 + 2], this.footprints, o, l.swath)
      o += FOOTPRINT_FLOATS
    }
    for (const lon of nominal) {
      const rad = (lon * Math.PI) / 180
      const r = GLOBE_RADIUS_KM + GEO_ALT_KM
      writeHorizonFootprint(r * Math.cos(rad), r * Math.sin(rad), 0, this.footprints, o)
      o += FOOTPRINT_FLOATS
      writeGroundCircle(Math.cos(rad), Math.sin(rad), 0, NOMINAL_MARK_KM / GLOBE_RADIUS_KM, this.footprints, o)
      o += FOOTPRINT_FLOATS
    }
    this.footprintsVersion++
  }

  /** Advances to the clock's time. The browser's loop calls it; the headset calls it per XR frame. */
  tick(): void {
    if (!this.settings.on) return
    this.engine.update(this.clock.time(), this.clock.speed)
    this.refreshSources()
    if (this.selected !== null) void this.refreshOrbit()
    this.refreshFootprints()
    this.emitFrame()
  }

  private startLoop(): void {
    if (this.raf || typeof requestAnimationFrame === 'undefined') return
    const loop = () => {
      this.raf = 0
      if (!this.settings.on) return
      this.tick()
      this.raf = requestAnimationFrame(loop)
    }
    this.raf = requestAnimationFrame(loop)
  }

  private stop(): void {
    if (this.raf && typeof cancelAnimationFrame !== 'undefined') cancelAnimationFrame(this.raf)
    this.raf = 0
    if (this.pollTimer) clearInterval(this.pollTimer)
    this.pollTimer = null
    this.emitFrame()
  }

  /** Settings or data changed (redraw panels). */
  onChange(fn: () => void): () => void {
    this.listeners.add(fn)
    return () => this.listeners.delete(fn)
  }

  /** Positions moved (redraw the globe). */
  onFrame(fn: () => void): () => void {
    this.frameListeners.add(fn)
    return () => this.frameListeners.delete(fn)
  }

  private emit(): void {
    for (const fn of this.listeners) fn()
  }

  private emitFrame(): void {
    for (const fn of this.frameListeners) fn()
  }
}

/** Newest first; curated items before automatic ones on the same day. */
export function sortEvents(events: SatelliteEvent[]): SatelliteEvent[] {
  return [...events]
    .filter((e) => e && typeof e.id === 'string' && e.title && typeof e.title.en === 'string')
    .sort((a, b) => (a.date === b.date ? Number(a.auto) - Number(b.auto) : a.date < b.date ? 1 : -1))
}

/** Starts the SGP4 worker on its first message, so a page with satellites off never loads it. */
class LazyWorkerPort implements SatellitePropagatorPort {
  onmessage: SatellitePropagatorPort['onmessage'] = null
  private worker: Worker | null = null

  postMessage(msg: Parameters<SatellitePropagatorPort['postMessage']>[0], transfer: Transferable[] = []): void {
    if (!this.worker) {
      this.worker = new Worker(new URL('./satellitePropagator.worker.ts', import.meta.url), { type: 'module' })
      this.worker.onmessage = (ev) => this.onmessage?.(ev)
    }
    this.worker.postMessage(msg, transfer)
  }

  terminate(): void {
    this.worker?.terminate()
    this.worker = null
  }
}

let shared: SatelliteLayer | null = null

/** The page's Satellites layer, created on first use. */
export function getSatelliteLayer(): SatelliteLayer {
  if (!shared) shared = new SatelliteLayer(new LazyWorkerPort())
  return shared
}
